/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-03-27 04:09:19
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { ButtonGroup, Button, Icon } from '@shopify/polaris';
import {
  MobileVerticalDotsMajor,
  MinusMinor,
  CaretUpMinor,
  CaretDownMinor
} from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect2 } from 'src/components/views/common/render-select-2-v2';
import { RenderTextField } from 'src/components/views/common/render-textfield';
import CollectionItem from './collection-item';

type Props = WithI18nProps;

class CollectionDetails extends Component<Props> {
  render() {
    const { provided, snapshot, collectionOptions, routePath, i18n } = this.props;
    const collection = this.getValue();

    return (
      <div className="rowTag" ref={provided.innerRef} {...provided.draggableProps}>
        {/* Drag&Drop icon*/}
        <CollectionItem isDragOccurring={snapshot.isDragging} className="dragIcon">
          <div {...provided.dragHandleProps}>
            <Icon color="subdued" source={MobileVerticalDotsMajor} />
          </div>
        </CollectionItem>

        {/* Show/hide sub tags button */}
        <CollectionItem isDragOccurring={snapshot.isDragging} className="dragShowHideBtn">
          {collection.tags && collection.tags.length > 0 ? (
            <Button
              plain
              onClick={() => {
                this.props.showHideSubTags(routePath);
              }}>
              {collection.expanded ? (
                <Icon source={CaretDownMinor} />
              ) : (
                <Icon source={CaretUpMinor} />
              )}
            </Button>
          ) : (
            <Button plain disabled onClick={() => {}}>
              <Icon source={CaretDownMinor} />
            </Button>
          )}
        </CollectionItem>

        {/* Tag */}
        <CollectionItem isDragOccurring={snapshot.isDragging} className="dragSelectTag">
          <RenderSelect2
            component={RenderSelect2}
            options={collectionOptions}
            placeholder={i18n.translate(
              'AdminHTML.FilterTree.Item.optionModal_GeneralTab_Collection_CollectionDetail_RenderSelect2_Placeholder'
            )}
            value={collection}
            onChange={this.onChangeCollection}
          />
        </CollectionItem>

        {/* Display name */}
        <CollectionItem isDragOccurring={snapshot.isDragging} className="dragDisplayName">
          <RenderTextField
            component={RenderTextField}
            placeholder={i18n.translate('AdminHTML.FilterTree.Common.name')}
            value={collection.displayName}
            onChange={this.onChangeDisplayName}
          />
        </CollectionItem>

        {/* Action */}
        <CollectionItem isDragOccurring={snapshot.isDragging} className="action">
          {this.renderAction()}
        </CollectionItem>
      </div>
    );
  }

  renderAction = () => {
    const { routePath, total } = this.props;

    return total > 1 ? (
      <ButtonGroup segmented>
        <Button outline onClick={() => this.props.removeCollection(routePath)} icon={MinusMinor} />
      </ButtonGroup>
    ) : (
      ''
    );
  };

  parseRoutePath = routePath => {
    const parsed = routePath.split('/');
    return `[${parsed[0]}]`;
  };

  // Get collection based on route path
  getValue = () => {
    const { formValues, routePath } = this.props;

    const accessPath = this.parseRoutePath(routePath);

    // Get collection
    const collection = _.get(formValues, accessPath, {});

    // If collection is not selected, return empty string that allows to show placeholder
    return _.get(collection, 'handle') ? collection : {};
  };

  // Update change at specific index based on route path
  updateChange = (field, value, updatedList, routePath, force = true) => {
    const parsed = routePath.split('/');

    if (!_.isArray(parsed) || parsed[0] === '') return updatedList;

    // Change collection at level 1
    const currentValue = updatedList[parsed[0]];
    const isEmpty =
      field === 'displayName' ? _.isEmpty(_.get(currentValue, field)) : _.isEmpty(currentValue);
    if (force || (!force && isEmpty)) {
      updatedList[parsed[0]] = _.merge(updatedList[parsed[0]], value);
    }

    return updatedList;
  };

  // Handle change tag
  onChangeCollection = collection => {
    const { formValues, routePath } = this.props;
    const { label } = collection;

    let updatedList = this.updateChange('tag', collection, [...formValues], routePath);
    updatedList = this.updateChange(
      'displayName',
      { displayName: label },
      [...updatedList],
      routePath,
      false
    );

    this.props.updateManualValues(updatedList);
  };

  // Handle change display name
  onChangeDisplayName = name => {
    const { formValues, routePath } = this.props;

    const updatedList = this.updateChange(
      'displayName',
      { displayName: name },
      [...formValues],
      routePath
    );

    this.props.updateManualValues(updatedList);
  };
}

export default withI18n()(CollectionDetails);
